/**
 * The sync engine — Arch §15, LV §127-132, acceptance test 8 (LV §174).
 *
 * Arch §15 gives the sequence, and this module follows it in order:
 *
 *     store local changes → network returns → check auth → send pending changes
 *     → check server version → detect conflicts → confirm successful changes
 *     → download server changes
 *
 * Two ordering decisions matter and are not arbitrary:
 *
 * 1. **Photos upload before the records that reference them.** An inspection result
 *    carries a `photo_document_id`, and that column is a real foreign key — a record
 *    arriving before its photo would be refused by the database. LV §174 asks for
 *    three photos to end up attached to the correct asset, and this ordering is what
 *    makes that possible.
 *
 * 2. **A `409` stops that record, not the queue.** One conflicted work order must not
 *    block a technician's other five completions from reaching the server.
 */

import { ApiError, request } from "@/services/http";
import { getToken } from "@/auth/session";
import { offlineEnabled } from "@/app/feature-flags";
import {
  type LocalRecord,
  type SyncState,
  countByState,
  markConflict,
  markError,
  markPhotoError,
  markPhotoSynced,
  markSynced,
  pendingPhotos,
  photoBlob,
  pendingRecords,
  photosForRecord,
  recordForPhoto,
  releasePhoto,
  syncedPhotos,
} from "@/offline/db";

export interface SyncResult {
  uploaded: number;
  photos_uploaded: number;
  conflicts: number;
  errors: number;
  skipped_offline: boolean;
  /** Whether Arch §15's last step ran — server changes were pulled back in. */
  downloaded: boolean;
  /** German, for the status banner. */
  message: string;
}

export interface SyncStatus {
  online: boolean;
  syncing: boolean;
  last_sync_at: string | null;
  counts: Record<SyncState, number>;
}

type StatusListener = (status: SyncStatus) => void;

/** Arch §15's last step, supplied by the application. See `setChangeDownloader`. */
type ChangeDownloader = () => void | Promise<void>;

let syncing = false;
let lastSyncAt: string | null = null;
let downloadChanges: ChangeDownloader | null = null;
const listeners = new Set<StatusListener>();

/** Retries stop here. The record is kept and surfaced, never dropped. */
const MAX_ATTEMPTS = 5;

/**
 * Marks a request as a replay out of the offline queue — LV §181.
 *
 * The queue replays *ordinary* endpoints: a completed inspection goes to
 * `POST /inspections/{id}/complete` whether it was captured in the office or in a
 * dead spot. There is no sync endpoint for the backend to close, so the request says
 * what it is and `app/core/features.py::guard_offline_replay` refuses it while
 * `FEATURE_OFFLINE` is off. The name must match the constant there exactly.
 */
export const OFFLINE_REPLAY_HEADER = "X-DAH-Offline-Replay";

const REPLAY_HEADERS: Record<string, string> = { [OFFLINE_REPLAY_HEADER]: "1" };

/** The refusal the backend sends when offline operation is switched off (LV §181). */
const FEATURE_DISABLED_CODE = "FEATURE_DISABLED";

export function onSyncStatus(listener: StatusListener): () => void {
  listeners.add(listener);
  void publish();
  return () => listeners.delete(listener);
}

async function publish(): Promise<void> {
  const status: SyncStatus = {
    online: navigator.onLine,
    syncing,
    last_sync_at: lastSyncAt,
    counts: await countByState(),
  };
  for (const listener of listeners) listener(status);
}

export async function getSyncStatus(): Promise<SyncStatus> {
  return {
    online: navigator.onLine,
    syncing,
    last_sync_at: lastSyncAt,
    counts: await countByState(),
  };
}

/**
 * Run one synchronisation pass.
 *
 * Safe to call repeatedly: a record already `SYNCED` is not in the pending query, and
 * the guard prevents two passes overlapping. That matters because the trigger fires on
 * `online`, on app focus and on an interval, and all three can coincide.
 */
/**
 * Register Arch §15's last step: download server changes.
 *
 * The engine deliberately knows nothing about TanStack Query. Reading is the
 * application's business — the queue's own job ends when the server holds the record —
 * so `main.tsx` supplies the step and it means "invalidate the cache" in a client whose
 * reads are queries. Without it the sequence stopped one step short: a completion left
 * the tablet, and the order on screen still showed the status it had before, because
 * nothing had told the cache that anything happened (LV §130).
 */
export function setChangeDownloader(handler: ChangeDownloader | null): void {
  downloadChanges = handler;
}

/**
 * Pull the server's state back in, and say whether it happened.
 *
 * A failing download does not fail the pass: the records are on the server either way,
 * and the query layer refetches on its own schedule. It must not throw out of
 * `synchronise` and leave `syncing` wedged.
 */
async function downloadServerChanges(): Promise<boolean> {
  if (!downloadChanges) return false;
  try {
    await downloadChanges();
    return true;
  } catch {
    return false;
  }
}

export async function synchronise(): Promise<SyncResult> {
  if (syncing) {
    return {
      uploaded: 0,
      photos_uploaded: 0,
      conflicts: 0,
      errors: 0,
      skipped_offline: false,
      downloaded: false,
      message: "Synchronisation läuft bereits.",
    };
  }

  // Step 0 — is offline operation switched on at all (LV §181)?
  //
  // Ahead of the network check on purpose: with the flag off there is no queue to
  // drain and the backend refuses a replay anyway, so a pass would achieve nothing
  // but a row of identical 422s against records the technician can still see.
  if (!offlineEnabled()) {
    return {
      uploaded: 0,
      photos_uploaded: 0,
      conflicts: 0,
      errors: 0,
      skipped_offline: false,
      downloaded: false,
      message: "Offline-Betrieb ist in dieser Installation nicht aktiviert.",
    };
  }

  // Step 2 — network returns.
  if (!navigator.onLine) {
    return {
      uploaded: 0,
      photos_uploaded: 0,
      conflicts: 0,
      errors: 0,
      skipped_offline: true,
      downloaded: false,
      message: "Keine Verbindung. Änderungen bleiben lokal gespeichert.",
    };
  }

  // Step 3 — check auth. Uploading without a token would produce a queue of 401s and
  // burn the retry budget on every pending record.
  if (!getToken()) {
    return {
      uploaded: 0,
      photos_uploaded: 0,
      conflicts: 0,
      errors: 0,
      skipped_offline: false,
      downloaded: false,
      message: "Anmeldung erforderlich, um Änderungen zu übertragen.",
    };
  }

  syncing = true;
  await publish();

  let uploaded = 0;
  let photosUploaded = 0;
  let conflicts = 0;
  let errors = 0;
  let downloaded = false;

  try {
    // Photos first — see the module docstring.
    photosUploaded = await uploadPhotos();

    // Step 4 — send pending changes.
    for (const record of await pendingRecords()) {
      const outcome = await uploadRecord(record);
      if (outcome === "uploaded") uploaded += 1;
      else if (outcome === "conflict") conflicts += 1;
      else errors += 1;
      // The server has switched offline operation off since this client last asked
      // (LV §181). Every remaining record would be refused the same way, so the pass
      // stops here rather than marking the whole queue with the same message.
      if (outcome === "disabled") break;
    }

    await releaseConfirmedPhotos();

    // Step 8 — download server changes (Arch §15's last step).
    //
    // Only when the pass actually moved something. An upload changes state no client
    // can derive — the order's status, a material's stock, the cost roll-up — and a
    // conflict means the server holds a version newer than the one on screen. A pass
    // that found nothing to send changed nothing, and invalidating there would turn the
    // sixty-second timer into a sixty-second refetch of every open screen.
    if (uploaded > 0 || conflicts > 0) downloaded = await downloadServerChanges();

    lastSyncAt = new Date().toISOString();
  } finally {
    syncing = false;
    await publish();
  }

  return {
    uploaded,
    photos_uploaded: photosUploaded,
    conflicts,
    errors,
    skipped_offline: false,
    downloaded,
    message: buildMessage(uploaded, photosUploaded, conflicts, errors),
  };
}

function buildMessage(
  uploaded: number,
  photos: number,
  conflicts: number,
  errors: number,
): string {
  if (uploaded === 0 && photos === 0 && conflicts === 0 && errors === 0) {
    return "Alles synchronisiert.";
  }
  const parts: string[] = [];
  if (uploaded > 0) parts.push(`${uploaded} Änderung(en) übertragen`);
  if (photos > 0) parts.push(`${photos} Foto(s) übertragen`);
  if (conflicts > 0) parts.push(`${conflicts} Konflikt(e) — Klärung erforderlich`);
  if (errors > 0) parts.push(`${errors} Fehler`);
  return `${parts.join(", ")}.`;
}

/**
 * Upload the photos captured offline.
 *
 * A photo that fails is marked and skipped rather than retried in a loop — a
 * corrupted blob would otherwise stall every subsequent upload behind it.
 */
async function uploadPhotos(): Promise<number> {
  let uploaded = 0;
  for (const photo of await pendingPhotos()) {
    try {
      const form = new FormData();
      form.append(
        "file",
        new File([photoBlob(photo)], photo.filename, { type: photo.mime_type }),
      );
      form.append("title", `Foto ${photo.filename}`);
      form.append("document_type", "PHOTO");
      if (photo.asset_id) {
        // LV §174 checks the photos land on the correct asset, so the link is made at
        // upload time rather than inferred later.
        form.append("entity_type", "ASSET");
        form.append("entity_id", photo.asset_id);
        form.append("relationship_type", "PHOTO");
      }

      const created = await request<{ id: string }>("/documents", {
        method: "POST",
        formData: form,
        // A photo taken during a walkdown is offline work like any other change,
        // and is marked as such so the same gate covers it (LV §181).
        headers: REPLAY_HEADERS,
      });
      await markPhotoSynced(photo.local_id, created.id);
      // The local copy is *not* freed here — see `releaseConfirmedPhotos`.
      uploaded += 1;
    } catch {
      await markPhotoError(photo.local_id);
    }
  }
  return uploaded;
}

/**
 * Free the local copies of photos the server has confirmed — LV §132.
 *
 * Deliberately *after* the record loop, not inside `uploadPhotos`. LV §132 releases a
 * photo "only after the server confirms", and the confirmation that matters is not the
 * photo's own upload but the record that references it: while that record is still
 * pending, retrying it needs the local-id → document-id mapping these rows carry.
 * Freeing them at upload time made a retry send local ids the server has never seen.
 *
 * So a photo is released when its record reached the server, or when it has no record
 * (a standalone upload). A photo belonging to a `CONFLICT` or `ERROR` record stays.
 */
async function releaseConfirmedPhotos(): Promise<void> {
  for (const photo of await syncedPhotos()) {
    // Matched on either identity: the parent is usually the entity's server id, not the
    // record's local id. Looking only at the local id found no record, treated the photo
    // as parentless, and freed it — which destroyed the id mapping a retry needs.
    const parent = await recordForPhoto(photo);
    if (parent && parent.sync_state !== "SYNCED") continue;
    await releasePhoto(photo.local_id);
  }
}

type Outcome = "uploaded" | "conflict" | "error" | "disabled";

/**
 * Upload one record.
 *
 * Step 5-7 of Arch §15's sequence: send it, let the *server* check the version, and
 * turn a refusal into a `CONFLICT` the user can see rather than a lost edit.
 */
async function uploadRecord(record: LocalRecord): Promise<Outcome> {
  if (record.attempts >= MAX_ATTEMPTS) {
    await markError(
      record.local_id,
      "MAX_ATTEMPTS_REACHED",
      `Übertragung nach ${MAX_ATTEMPTS} Versuchen nicht möglich. Bitte manuell prüfen.`,
    );
    return "error";
  }

  const payload = await resolvePhotoReferences(record);

  try {
    const response = await request<{ id?: string }>(record.operation.path, {
      method: record.operation.method,
      body: payload,
      // Says what this is: a change captured offline and replayed. The backend
      // refuses it while `FEATURE_OFFLINE` is off (LV §181, §134).
      headers: REPLAY_HEADERS,
    });
    await markSynced(record.local_id, response?.id ?? record.server_id);
    return "uploaded";
  } catch (error) {
    if (!(error instanceof ApiError)) {
      await markError(
        record.local_id,
        "UNKNOWN",
        "Unerwarteter Fehler bei der Übertragung.",
      );
      return "error";
    }

    // Still offline — leave it pending. Marking it ERROR would need a manual retry
    // for something that will succeed on its own.
    if (error.isOffline) return "error";

    // Step 6 — detect conflicts (LV §105, §176). The local payload is kept, the
    // server's state is fetched beside it, and the user resolves.
    if (error.isConflict) {
      const serverState = await fetchServerState(record);
      await markConflict(
        record.local_id,
        serverState,
        "Der Datensatz wurde zwischenzeitlich von einem anderen Benutzer geändert.",
      );
      return "conflict";
    }

    // A business-rule refusal (422) will fail identically on every retry, so it is an
    // ERROR to show the user with the server's German message — not something to
    // retry until the attempt budget runs out.
    await markError(record.local_id, error.code, error.message);
    // One 422 is not like the others: `FEATURE_DISABLED` says the installation no
    // longer accepts replayed offline work at all, which is a statement about the
    // queue rather than about this record (LV §181). The record is kept and shown
    // like any other error; the caller stops the pass.
    return error.code === FEATURE_DISABLED_CODE ? "disabled" : "error";
  }
}

/**
 * Replace local photo ids in a payload with the server document ids.
 *
 * An inspection captured offline references its photos by local id, because the server
 * ids do not exist yet. By the time the record uploads the photos already have them,
 * and this is where the substitution happens.
 */
async function resolvePhotoReferences(
  record: LocalRecord,
): Promise<Record<string, unknown>> {
  const photos = await photosForRecord(record);
  if (photos.length === 0) return record.payload;

  const mapping = new Map<string, string>();
  for (const photo of photos) {
    if (photo.server_document_id) mapping.set(photo.local_id, photo.server_document_id);
  }
  if (mapping.size === 0) return record.payload;

  const substitute = (value: unknown): unknown => {
    if (typeof value === "string") return mapping.get(value) ?? value;
    if (Array.isArray(value)) return value.map(substitute);
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([key, item]) => [
          key,
          substitute(item),
        ]),
      );
    }
    return value;
  };

  return substitute(record.payload) as Record<string, unknown>;
}

/**
 * Fetch the server's current state for a conflicted record.
 *
 * Arch §15: "the user must be able to see the differences". Without the server's
 * version there is nothing to diff against, so a failure here still records the
 * conflict — with an empty comparison rather than none at all.
 */
async function fetchServerState(record: LocalRecord): Promise<Record<string, unknown>> {
  if (!record.server_id) return {};
  try {
    return await request<Record<string, unknown>>(resourcePath(record));
  } catch {
    return {};
  }
}

/**
 * The GET path for the record's own resource.
 *
 * Derived by truncating the action off the operation path rather than by appending the
 * id to a collection: an action path already contains the id
 * (`/work-orders/{id}/technical-complete`), and appending it again produced
 * `/work-orders/{id}/{id}` — a 404, which surfaced as a conflict with an empty
 * comparison instead of the server's version.
 */
function resourcePath(record: LocalRecord): string {
  const segments = record.operation.path.split("/");
  const idIndex = record.server_id ? segments.indexOf(record.server_id) : -1;
  if (idIndex > 0) return segments.slice(0, idIndex + 1).join("/");
  // A creating POST against a collection — compare with the parent resource.
  return `${segments.slice(0, 2).join("/")}/${record.server_id}`;
}

/**
 * Wire the automatic triggers.
 *
 * Three of them, because each catches a case the others miss: `online` for the tablet
 * leaving a dead spot, `visibilitychange` for the app being brought back to the
 * foreground, and the interval for a long-running session that never loses focus.
 *
 * None of them is wired when `FEATURE_OFFLINE` is off (LV §181): with offline
 * operation switched off the application is an ordinary online one, and an engine
 * polling every sixty seconds for a queue that is never written to is exactly the
 * "the flag disables nothing" state this was changed to fix. The flag has to be
 * loaded before this is called — `main.tsx` does that in `bootstrapSession`.
 */
export function startAutoSync(intervalMs = 60_000): () => void {
  if (!offlineEnabled()) return () => {};

  const attempt = (): void => {
    void synchronise();
  };

  const onOnline = (): void => {
    void publish();
    attempt();
  };
  const onOffline = (): void => {
    void publish();
  };
  const onVisible = (): void => {
    if (document.visibilityState === "visible" && navigator.onLine) attempt();
  };

  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);
  document.addEventListener("visibilitychange", onVisible);
  const timer = window.setInterval(attempt, intervalMs);

  if (navigator.onLine) attempt();

  return () => {
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
    document.removeEventListener("visibilitychange", onVisible);
    window.clearInterval(timer);
  };
}
